import SanityBuilder from '@sanity/image-url'
import type {
  ImageUrlBuilderOptions,
  SanityImageObject,
  SanityImageSource,
} from '@sanity/image-url/lib/types/types'
import sanityClient from '@/clients/sanity'
import { ASPECT_RATIO_MAP, generateSrcsetEntry, getRatio } from '@/utils/media-transformer'
import type { AspectRatioInterface } from '@/types/generic'
import type { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder'
export interface TransformParams extends ImageUrlBuilderOptions {
  ratio?: keyof AspectRatioInterface
}

export interface ExtendedSanityImageSource extends SanityImageObject {
  alt: string
  originalFilename: string
  extension: string
  ratio?: keyof AspectRatioInterface
}

const imgBuilder = (source: SanityImageSource) =>
  SanityBuilder({
    // @ts-expect-error - baseUrl is not in the types but it is in the source code & works as expected
    baseUrl: 'https://asset.jamieoliver.com',
    ...sanityClient.config(),
  }).image(source)

export const buildSrc = (source: ExtendedSanityImageSource, transforms: TransformParams) => {
  if (!source) return

  let builder = imgBuilder(source)

  transforms = {
    auto: 'format',
    format: 'webp',
    fit: 'crop',
    quality: 75,
    width: 1040,
    ...transforms,
  }

  if (transforms) {
    // based off of Sanity Image Transformations
    // https://www.sanity.io/docs/image-url
    if (transforms.auto) builder = builder.auto(transforms.auto)
    if (transforms.format) builder = builder.format(transforms.format)

    if (transforms.dpr) builder = builder.dpr(transforms.dpr)
    if (transforms.quality) builder = builder.quality(transforms.quality)

    if (transforms.width) builder = builder.width(transforms.width)
    if (transforms.height) builder = builder.height(transforms.height)

    if (transforms.fit) builder = builder.fit(transforms.fit)
    if (transforms.crop) builder = builder.crop(transforms.crop)
    // Specify the crop in pixels. Overrides any crop/hotspot in the image record.
    if (transforms.rect) {
      const { left, top, width, height } = transforms.rect
      builder = builder.rect(left, top, width, height)
    }

    if (transforms.blur) builder = builder.blur(transforms.blur)

    if (transforms.ratio && transforms.width && ASPECT_RATIO_MAP[transforms.ratio]) {
      const { ratio, width } = transforms
      const height = Math.round((getRatio(ratio).height / getRatio(ratio).width) * width)
      builder = builder.size(width, height)
    }
  }

  const extendedBuilder = builder as {
    vanityUrl: () => string
  } & ImageUrlBuilder

  extendedBuilder.vanityUrl = () => {
    const { originalFilename } = source

    if (!originalFilename) {
      return extendedBuilder.url()
    }

    const url = extendedBuilder.url()
    const [baseUrl, query] = url.split('?')

    return encodeURI(`${baseUrl}/${originalFilename}?${query}`)
  }

  return extendedBuilder
}

export const buildSrcSetUrls = (
  source: ExtendedSanityImageSource,
  transforms: TransformParams,
  widths: number[],
): string => {
  if (!source || !widths?.length) return ''

  return widths
    .reduce((acc: string[], cur: number): string[] => {
      const src = buildSrc(source, { ...transforms, width: cur })
      if (!src) return acc
      return [...acc, generateSrcsetEntry(src.vanityUrl(), cur)]
    }, [])
    .join(', ')
}
